// client/src/api/automation.api.js

const API_BASE = '/api/v1';

function getAuthHeaders() {
  const token = localStorage.getItem('tkn');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// --- Scheduled Flows ---
export async function getScheduledFlows() {
  const res = await fetch(`${API_BASE}/scheduled-flows`, {
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch scheduled flows');
  return data.flows;
}

export async function createScheduledFlow(flow) {
  const res = await fetch(`${API_BASE}/scheduled-flows`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(flow),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to create scheduled flow');
  return data.flow;
}

export async function updateScheduledFlow(id, flow) {
  const res = await fetch(`${API_BASE}/scheduled-flows/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(flow),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to update scheduled flow');
  return data.flow;
}

export async function deleteScheduledFlow(id) {
  const res = await fetch(`${API_BASE}/scheduled-flows/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to delete scheduled flow');
  return true;
}

// --- Templates ---
export async function getTemplates() {
  const res = await fetch('/api/v1/workflow-templates', {
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch templates');
  if (!data.templates || data.templates.length === 0) {
    // Return mock data if backend returns no rows
    return [
      { id: '1', name: 'Lead Qualification', description: 'Qualify leads using AI and CRM data.' },
      { id: '2', name: 'Customer Onboarding', description: 'Automate onboarding steps for new customers.' },
      { id: '3', name: 'Support Ticket Triage', description: 'Route and prioritize support tickets.' },
    ];
  }
  return data.templates;
}

export async function createTemplate(template) {
  const res = await fetch('/api/v1/workflow-templates', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(template),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to create template');
  return data.template;
}

export async function updateTemplate(id, template) {
  const res = await fetch(`/api/v1/workflow-templates/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(template),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to update template');
  return data.template;
}

export async function deleteTemplate(id) {
  const res = await fetch(`/api/v1/workflow-templates/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to delete template');
  return true;
}

// --- Flow Executions ---
export async function getFlowExecutions() {
  // Returns executions with nodeTelemetry for each execution
  const res = await fetch('/api/v1/flow-executions', {
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch executions');
  return data.executions;
}

export async function updateNodeTelemetry(executionId, nodeTelemetry) {
  const res = await fetch(`/api/v1/flow-executions/${executionId}/node-telemetry`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ nodeTelemetry }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to update node telemetry');
  return data.execution;
}

export async function updateAutomationRule(id, rule) {
  const res = await fetch(`/api/v1/automation-rules/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(rule),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to update rule');
  return data.rule;
}

export async function deleteAutomationRule(id) {
  const res = await fetch(`/api/v1/automation-rules/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to delete rule');
  return true;
}

export async function getAutomationRules() {
  const res = await fetch('/api/v1/automation-rules', {
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch rules');
  return data.rules;
}

export async function createAutomationRule(rule) {
  const res = await fetch('/api/v1/automation-rules', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(rule),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to create rule');
  return data.rule;
}

export async function getAutomationRuleHistory(id) {
  const res = await fetch(`/api/v1/automation-rules/${id}/history`, {
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch rule history');
  return data.history;
}

export async function testAutomationRule(id) {
  const res = await fetch(`/api/v1/automation-rules/${id}/test`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Failed to test rule');
  return data.result;
}
